import { MutationOptions, WithRequired } from '@tanstack/react-query';

import {
  createSPOptimizerStoreSamples,
  deleteSPOptimizerStoreExcludePhrase,
  deleteSPOptimizerStoreIncludePhrase,
  getSignedEmailScreenshotUploadUrl,
  getSignedLogoUploadUrlForUser,
  getSignedMediaUploadUrlForUseCase,
  patchSPOptimizerStore,
  patchStoreInfo,
  patchUsecaseOnboardingState,
  postConvertSPOptimizerFlowMessageExperimentToMab,
  postCreateSPOptimizerFlowMessageExperimentFromFlowMessage,
  postCreateSPOptimizerFlowMessageExperimentPhaseFromFlowMessage,
  postCreateSPOptimizerStoreFromFlowMessage,
  postCreateSPOptimizerStoreFromStore,
  postCreateUsecasesFromOnboarding,
  postDidUploadEmailScreenshot,
  postDidUploadLogoForUser,
  postDidUploadUsecaseMedia,
  postFeedback,
  postSPOptimizerFlowMessageExperimentPhaseSetWinnerShard,
  postSPOptimizerFlowMessageExperimentSetWinnerShard,
  postSPOptimizerStoreBulkCreateNewPhase,
  postSPOptimizerStoreExcludePhrase,
  postSPOptimizerStoreIncludePhrase,
  postSaveSelectedProduct,
  postSaveSpOptimizerLiteSetCustomerEmailAndMessageType,
  postSaveVideoScripts,
  postStoreInfo,
  postTriggerVariantGeneration,
} from './methods';

const makeMutation = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions?: Omit<MutationOptions<Awaited<ReturnType<F>>, unknown, Parameters<F>>, 'mutationFn'>,
): WithRequired<MutationOptions<Awaited<ReturnType<F>>, unknown, Parameters<F>>, 'mutationFn'> => {
  return {
    mutationFn: (args) => apiMethod(...args),
    ...(otherOptions ?? {}),
  };
};

export const mutations = {
  signedLogoUploadUrlForUser: makeMutation(getSignedLogoUploadUrlForUser),
  signedEmailScreenshotUploadUrl: makeMutation(getSignedEmailScreenshotUploadUrl),
  didUploadEmailScreenshot: makeMutation(postDidUploadEmailScreenshot),
  triggerVariantGeneration: makeMutation(postTriggerVariantGeneration),
  signedMediaUploadUrlForUseCase: makeMutation(getSignedMediaUploadUrlForUseCase),
  didUploadUseCaseMedia: makeMutation(postDidUploadUsecaseMedia),
  didUploadLogoForUser: makeMutation(postDidUploadLogoForUser),
  usecaseOnboardingState: makeMutation(patchUsecaseOnboardingState),
  saveVideoScript: makeMutation(postSaveVideoScripts),
  saveSpOptimizerLiteSetCustomerEmailAndMessageType: makeMutation(
    postSaveSpOptimizerLiteSetCustomerEmailAndMessageType,
  ),
  createSPOptimizerStoreFromFlowMessage: makeMutation(postCreateSPOptimizerStoreFromFlowMessage),
  createSPOptimizerStoreFromStore: makeMutation(postCreateSPOptimizerStoreFromStore),
  createSPOptimizerStoreBulkCreateNewPhase: makeMutation(postSPOptimizerStoreBulkCreateNewPhase),
  createSPOptimizerFlowMessageExperimentFromFlowMessage: makeMutation(
    postCreateSPOptimizerFlowMessageExperimentFromFlowMessage,
  ),
  convertSPOptimizerFlowMessageExperimentToMab: makeMutation(postConvertSPOptimizerFlowMessageExperimentToMab),
  createSPOptimizerFlowMessageExperimentPhaseFromFlowMessage: makeMutation(
    postCreateSPOptimizerFlowMessageExperimentPhaseFromFlowMessage,
  ),
  updateSPOptimizerFlowMessageExperimentSetWinnerShard: makeMutation(
    postSPOptimizerFlowMessageExperimentSetWinnerShard,
  ),
  updateSPOptimizerFlowMessageExperimentPhaseSetWinnerShard: makeMutation(
    postSPOptimizerFlowMessageExperimentPhaseSetWinnerShard,
  ),
  createStoreInfo: makeMutation(postStoreInfo),
  createSPOptimizerStoreExcludePhrase: makeMutation(postSPOptimizerStoreExcludePhrase),
  createSPOptimizerStoreIncludePhrase: makeMutation(postSPOptimizerStoreIncludePhrase),
  deleteSPOptimizerStoreExcludePhrase: makeMutation(deleteSPOptimizerStoreExcludePhrase),
  deleteSPOptimizerStoreIncludePhrase: makeMutation(deleteSPOptimizerStoreIncludePhrase),
  updateStoreInfo: makeMutation(patchStoreInfo),
  updateSPOptimizerStore: makeMutation(patchSPOptimizerStore),
  createUseCasesFromOnboarding: makeMutation(postCreateUsecasesFromOnboarding),
  createFeedback: makeMutation(postFeedback),
  createSPOptimizerStoreSamples: makeMutation(createSPOptimizerStoreSamples),
  saveSelectedProduct: makeMutation(postSaveSelectedProduct),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} satisfies Record<string, WithRequired<MutationOptions<any, unknown, any>, 'mutationFn'>>;
